import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { 
  ArrowRight, 
  ArrowLeft,
  Loader2, 
  Briefcase, 
  Users, 
  PenLine, 
  Sparkles,
  Check,
  SkipForward,
  FileText,
  Upload,
  File,
  Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Onboarding steps configuration
const STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Sparkles },
  { id: 'role', title: 'Your Role', icon: Briefcase },
  { id: 'team', title: 'Your Team', icon: Users },
  { id: 'style', title: 'Your Style', icon: PenLine },
  { id: 'sample', title: 'Writing Sample', icon: PenLine },
  { id: 'goals', title: 'Your Goals', icon: Sparkles },
  { id: 'knowledge', title: 'Knowledge Base', icon: FileText },
];

const OnboardingPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    // Role & Company
    name: '',
    jobTitle: '',
    company: '',
    industry: '',
    companySize: '',
    
    // Team & Communication
    reportsTo: '',
    directReports: '',
    mainCollaborators: '',
    externalContacts: '',
    
    // Communication Style
    formalityPreference: '',
    communicationGoals: '',
    tonePreferences: [],
    
    // Writing Sample
    writingSample: '',
    writingContext: '',
    
    // Goals & Preferences
    primaryGoals: '',
    biggestChallenges: '',
    workingStyle: '',
  });
  
  const navigate = useNavigate();
  const { completeOnboarding } = useAuth();

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDocUpload = async (file) => {
    if (!file) return;
    
    const allowedTypes = ['.pdf', '.docx', '.txt', '.md', '.csv'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(ext)) {
      toast.error(`Unsupported file type. Allowed: ${allowedTypes.join(', ')}`);
      return;
    }
    
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 25MB.');
      return;
    }
    
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/knowledge-docs`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadedDocs(prev => [...prev, {
        id: response.data.doc_id,
        filename: file.name,
        size: file.size
      }]);
      toast.success(`Uploaded: ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeDoc = async (docId) => {
    try {
      await axios.delete(`${API}/knowledge-docs/${docId}`);
      setUploadedDocs(prev => prev.filter(d => d.id !== docId));
      toast.success('Document removed');
    } catch (err) {
      toast.error('Failed to remove document');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Build comprehensive profile for AI
      const profile = {
        role_description: buildRoleDescription(),
        help_goals: buildHelpGoals(),
        full_profile: formData,
      };
      
      await completeOnboarding(profile);
      toast.success("Perfect! I'm ready to help you.");
      navigate('/chat');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const buildRoleDescription = () => {
    const parts = [];
    if (formData.name) parts.push(`My name is ${formData.name}.`);
    if (formData.jobTitle) parts.push(`I'm a ${formData.jobTitle}`);
    if (formData.company) parts.push(`at ${formData.company}`);
    if (formData.industry) parts.push(`in the ${formData.industry} industry.`);
    if (formData.companySize) parts.push(`Company size: ${formData.companySize}.`);
    if (formData.reportsTo) parts.push(`I report to ${formData.reportsTo}.`);
    if (formData.directReports) parts.push(`I manage: ${formData.directReports}.`);
    if (formData.mainCollaborators) parts.push(`I frequently work with: ${formData.mainCollaborators}.`);
    if (formData.externalContacts) parts.push(`External contacts: ${formData.externalContacts}.`);
    return parts.join(' ');
  };

  const buildHelpGoals = () => {
    const parts = [];
    if (formData.communicationGoals) parts.push(`Communication goals: ${formData.communicationGoals}.`);
    if (formData.formalityPreference) parts.push(`Formality preference: ${formData.formalityPreference}.`);
    if (formData.tonePreferences.length) parts.push(`Preferred tone: ${formData.tonePreferences.join(', ')}.`);
    if (formData.writingSample) parts.push(`Writing style sample provided.`);
    if (formData.primaryGoals) parts.push(`Primary goals: ${formData.primaryGoals}.`);
    if (formData.biggestChallenges) parts.push(`Challenges to help with: ${formData.biggestChallenges}.`);
    if (formData.workingStyle) parts.push(`Working style: ${formData.workingStyle}.`);
    return parts.join(' ');
  };

  const canProceed = () => {
    switch (STEPS[currentStep].id) {
      case 'welcome':
        return formData.name.trim().length > 0;
      case 'role':
        return formData.jobTitle.trim().length > 0;
      default:
        return true;
    }
  };

  const pageVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 }
  };

  const renderStep = () => {
    switch (STEPS[currentStep].id) {
      case 'welcome':
        return (
          <motion.div
            key="welcome"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <div className="relative inline-block mb-4">
                <div className="absolute -top-1.5 left-1">
                  <div className="bg-white text-background px-1.5 py-0.5 rounded text-[10px] font-bold leading-none" style={{ fontFamily: "'Coco Gothic', sans-serif" }}>
                    hey
                  </div>
                  <div className="absolute -bottom-0.5 left-2 w-1.5 h-1.5 bg-white transform rotate-45"></div>
                </div>
                <span className="text-5xl font-bold tracking-tight" style={{ fontFamily: "'Coco Gothic', sans-serif" }}>arlo</span>
              </div>
              <h1 className="text-2xl font-semibold tracking-tight font-[Manrope] mb-2">
                Let's get to know each other
              </h1>
              <p className="text-muted-foreground max-w-md mx-auto">
                I'll ask a few questions to understand how I can best help you. This takes about 2-3 minutes.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">What should I call you?</label>
                <Input
                  value={formData.name}
                  onChange={(e) => updateFormData('name', e.target.value)}
                  placeholder="Your first name"
                  className="text-base"
                  autoFocus
                  data-testid="name-input"
                />
              </div>
            </div>
          </motion.div>
        );

      case 'role':
        return (
          <motion.div
            key="role"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="space-y-6"
          >
            <div className="mb-6">
              <h2 className="text-xl font-semibold tracking-tight font-[Manrope] mb-2">
                Tell me about your role, {formData.name}
              </h2>
              <p className="text-muted-foreground">
                This helps me understand your context and communicate appropriately.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">What's your job title?</label>
                <Input
                  value={formData.jobTitle}
                  onChange={(e) => updateFormData('jobTitle', e.target.value)}
                  placeholder="e.g., Product Manager, Founder, Marketing Director"
                  className="text-base"
                  autoFocus
                  data-testid="job-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Company name <span className="text-muted-foreground">(optional)</span></label>
                <Input
                  value={formData.company}
                  onChange={(e) => updateFormData('company', e.target.value)}
                  placeholder="Where do you work?"
                  className="text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Industry <span className="text-muted-foreground">(optional)</span></label>
                <Input
                  value={formData.industry}
                  onChange={(e) => updateFormData('industry', e.target.value)}
                  placeholder="e.g., Tech, Healthcare, Finance, Education"
                  className="text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Company size <span className="text-muted-foreground">(optional)</span></label>
                <div className="grid grid-cols-3 gap-2">
                  {['1-10', '11-50', '51-200', '201-1000', '1000+', 'Solo'].map((size) => (
                    <button
                      key={size}
                      onClick={() => updateFormData('companySize', size)}
                      className={`py-2 px-3 rounded-lg border text-sm transition-all ${
                        formData.companySize === size
                          ? 'border-accent bg-accent/10 text-foreground'
                          : 'border-border hover:border-muted-foreground/50'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 'team':
        return (
          <motion.div
            key="team"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="space-y-6"
          >
            <div className="mb-6">
              <h2 className="text-xl font-semibold tracking-tight font-[Manrope] mb-2">
                Who do you work with?
              </h2>
              <p className="text-muted-foreground">
                Understanding your team helps me tailor communication for different audiences.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Who do you report to? <span className="text-muted-foreground">(title or name)</span></label>
                <Input
                  value={formData.reportsTo}
                  onChange={(e) => updateFormData('reportsTo', e.target.value)}
                  placeholder="e.g., CEO, VP of Product, Sarah (my manager)"
                  className="text-base"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Do you manage anyone?</label>
                <Input
                  value={formData.directReports}
                  onChange={(e) => updateFormData('directReports', e.target.value)}
                  placeholder="e.g., 3 engineers, marketing team of 5, no direct reports"
                  className="text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Who do you collaborate with most?</label>
                <Textarea
                  value={formData.mainCollaborators}
                  onChange={(e) => updateFormData('mainCollaborators', e.target.value)}
                  placeholder="e.g., Design team, Sales, Engineering leads, external vendors"
                  className="text-base min-h-[80px]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">External contacts? <span className="text-muted-foreground">(clients, investors, partners)</span></label>
                <Input
                  value={formData.externalContacts}
                  onChange={(e) => updateFormData('externalContacts', e.target.value)}
                  placeholder="e.g., Enterprise clients, VCs, Agency partners"
                  className="text-base"
                />
              </div>
            </div>
          </motion.div>
        );

      case 'style':
        return (
          <motion.div
            key="style"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="space-y-6"
          >
            <div className="mb-6">
              <h2 className="text-xl font-semibold tracking-tight font-[Manrope] mb-2">
                How do you like to communicate?
              </h2>
              <p className="text-muted-foreground">
                This helps me match your voice and tone preferences.
              </p>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-3">What's your default formality level?</label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { value: 'casual', label: 'Casual & friendly', desc: '"Hey! Quick update..."' },
                    { value: 'professional', label: 'Professional but warm', desc: '"Hi Sarah, hope you\'re well..."' },
                    { value: 'formal', label: 'Formal & polished', desc: '"Dear Mr. Johnson, I am writing to..."' },
                    { value: 'varies', label: 'It varies by audience', desc: 'I\'ll ask each time' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => updateFormData('formalityPreference', option.value)}
                      className={`text-left py-3 px-4 rounded-lg border transition-all ${
                        formData.formalityPreference === option.value
                          ? 'border-accent bg-accent/10'
                          : 'border-border hover:border-muted-foreground/50'
                      }`}
                    >
                      <div className="font-medium text-sm">{option.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{option.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-3">Select tones that describe your style:</label>
                <div className="flex flex-wrap gap-2">
                  {['Direct', 'Diplomatic', 'Enthusiastic', 'Reserved', 'Analytical', 'Empathetic', 'Concise', 'Detailed'].map((tone) => (
                    <button
                      key={tone}
                      onClick={() => {
                        const current = formData.tonePreferences;
                        if (current.includes(tone)) {
                          updateFormData('tonePreferences', current.filter(t => t !== tone));
                        } else {
                          updateFormData('tonePreferences', [...current, tone]);
                        }
                      }}
                      className={`py-1.5 px-3 rounded-full text-sm border transition-all ${
                        formData.tonePreferences.includes(tone)
                          ? 'border-accent bg-accent/10 text-foreground'
                          : 'border-border hover:border-muted-foreground/50 text-muted-foreground'
                      }`}
                    >
                      {formData.tonePreferences.includes(tone) && <Check className="inline h-3 w-3 mr-1" />}
                      {tone}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Anything specific about how you communicate?</label>
                <Textarea
                  value={formData.communicationGoals}
                  onChange={(e) => updateFormData('communicationGoals', e.target.value)}
                  placeholder="e.g., I tend to be too wordy - help me be concise. I avoid exclamation marks. I always include action items."
                  className="text-base min-h-[80px]"
                />
              </div>
            </div>
          </motion.div>
        );

      case 'sample':
        return (
          <motion.div
            key="sample"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="space-y-6"
          >
            <div className="mb-6">
              <h2 className="text-xl font-semibold tracking-tight font-[Manrope] mb-2">
                Show me how you write
              </h2>
              <p className="text-muted-foreground">
                Paste an email you've written so I can learn your voice. This is the most powerful way for me to match your style.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Paste a recent email you've sent <span className="text-muted-foreground">(any topic)</span></label>
                <Textarea
                  value={formData.writingSample}
                  onChange={(e) => updateFormData('writingSample', e.target.value)}
                  placeholder="Paste the body of an email here. It can be to a colleague, client, or anyone. I'll analyze your writing patterns, word choices, and structure."
                  className="text-base min-h-[160px] font-mono text-sm"
                  autoFocus
                  data-testid="writing-sample-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">What was the context? <span className="text-muted-foreground">(optional)</span></label>
                <Input
                  value={formData.writingContext}
                  onChange={(e) => updateFormData('writingContext', e.target.value)}
                  placeholder="e.g., Weekly update to my team, follow-up with a client"
                  className="text-base"
                />
              </div>

              <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Why this matters:</p>
                <p>Your writing sample helps me learn your vocabulary, sentence structure, how you open and close messages, and your overall voice. The more natural the sample, the better I can match your style.</p>
              </div>
            </div>
          </motion.div>
        );

      case 'goals':
        return (
          <motion.div
            key="goals"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="space-y-6"
          >
            <div className="mb-6">
              <h2 className="text-xl font-semibold tracking-tight font-[Manrope] mb-2">
                Almost there! What should I focus on?
              </h2>
              <p className="text-muted-foreground">
                Tell me what you need most help with so I can prioritize.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">What are your main goals right now?</label>
                <Textarea
                  value={formData.primaryGoals}
                  onChange={(e) => updateFormData('primaryGoals', e.target.value)}
                  placeholder="e.g., Launch a new product by Q2, improve team communication, get better at delegating, prepare for board meetings"
                  className="text-base min-h-[100px]"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">What's your biggest work challenge?</label>
                <Textarea
                  value={formData.biggestChallenges}
                  onChange={(e) => updateFormData('biggestChallenges', e.target.value)}
                  placeholder="e.g., Too many meetings, email overload, difficulty prioritizing, hard conversations with underperformers"
                  className="text-base min-h-[100px]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">How do you prefer to work?</label>
                <Textarea
                  value={formData.workingStyle}
                  onChange={(e) => updateFormData('workingStyle', e.target.value)}
                  placeholder="e.g., I like bullet points over paragraphs, I prefer to think out loud, I need help being more decisive, I work best with clear deadlines"
                  className="text-base min-h-[100px]"
                />
              </div>
            </div>
          </motion.div>
        );

      case 'knowledge':
        return (
          <motion.div
            key="knowledge"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="space-y-6"
          >
            <div className="mb-6">
              <h2 className="text-xl font-semibold tracking-tight font-[Manrope] mb-2">
                Upload work materials <span className="text-muted-foreground">(optional)</span>
              </h2>
              <p className="text-muted-foreground">
                Do you have any manuals, guidelines, templates, or educational materials that would help me assist you better?
              </p>
            </div>

            <div className="space-y-4">
              <div className="bg-muted/30 border border-dashed border-border rounded-xl p-6 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.txt,.md,.csv"
                  onChange={(e) => e.target.files?.[0] && handleDocUpload(e.target.files[0])}
                  className="hidden"
                />
                <FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm mb-3">
                  Upload documents for Arlo to reference in conversations
                </p>
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingDoc}
                  data-testid="upload-doc-btn"
                >
                  {uploadingDoc ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Choose Files
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground mt-3">
                  Supported: PDF, DOCX, TXT, MD, CSV (Max 25MB per file)
                </p>
              </div>

              {uploadedDocs.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Uploaded documents:</p>
                  {uploadedDocs.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <File className="h-4 w-4 text-blue-400" />
                        <div>
                          <p className="text-sm font-medium">{doc.filename}</p>
                          <p className="text-xs text-muted-foreground">{formatFileSize(doc.size)}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeDoc(doc.id)}
                        className="p-1.5 hover:bg-background rounded text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 text-sm">
                <p className="font-medium text-blue-400 mb-1">Examples of useful documents:</p>
                <ul className="text-muted-foreground space-y-1 text-sm">
                  <li>• Company style guides or brand guidelines</li>
                  <li>• Process documentation or SOPs</li>
                  <li>• Product specs or technical documentation</li>
                  <li>• Templates you frequently use</li>
                  <li>• Training materials or best practices</li>
                </ul>
              </div>

              <p className="text-sm text-muted-foreground text-center">
                You can always upload more documents later from the chat sidebar.
              </p>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="px-6 py-4 border-b border-border">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            {STEPS.map((step, idx) => {
              const Icon = step.icon;
              const isActive = idx === currentStep;
              const isComplete = idx < currentStep;
              return (
                <div
                  key={step.id}
                  className={`flex items-center ${idx > 0 ? 'ml-1' : ''}`}
                >
                  {idx > 0 && (
                    <div className={`w-8 h-0.5 mr-1 ${isComplete ? 'bg-accent' : 'bg-border'}`} />
                  )}
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                      isActive 
                        ? 'bg-accent text-accent-foreground' 
                        : isComplete 
                          ? 'bg-accent/20 text-accent' 
                          : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {isComplete ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </div>
                </div>
              );
            })}
          </div>
          <span className="text-sm text-muted-foreground">
            {currentStep + 1} of {STEPS.length}
          </span>
        </div>
      </header>

      {/* Progress bar */}
      <div className="px-6">
        <div className="max-w-2xl mx-auto">
          <Progress value={progress} className="h-1 mt-0 rounded-none bg-muted" />
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-6 py-8">
        <div className="w-full max-w-xl">
          <AnimatePresence mode="wait">
            {renderStep()}
          </AnimatePresence>
        </div>
      </main>

      {/* Footer navigation */}
      <footer className="px-6 py-4 border-t border-border">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div>
            {currentStep > 0 && (
              <Button
                variant="ghost"
                onClick={handleBack}
                className="gap-2"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            {currentStep > 0 && !isLastStep && (
              <Button
                variant="ghost"
                onClick={handleNext}
                className="text-muted-foreground"
                data-testid="skip-btn"
              >
                <SkipForward className="h-4 w-4 mr-1" />
                Skip
              </Button>
            )}
            
            {isLastStep ? (
              <Button
                onClick={handleComplete}
                disabled={loading}
                className="gap-2 bg-foreground text-background hover:bg-foreground/90"
                data-testid="complete-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Start using Arlo
                    <Sparkles className="h-4 w-4" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
                className="gap-2 bg-foreground text-background hover:bg-foreground/90"
                data-testid="next-btn"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OnboardingPage;
